import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { music24PlanCapabilities } from 'fym-shared/src/music24/music24Plans';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { SMLUpgradeModal } from '../../components/SMLUpgradeModal/SMLUpgradeModal';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { planNameMessages } from '../../config/music24PlanNames';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { planForStreamingDays } from '../streamingPeriod';
import { streamingMessages as messages } from '../streamingMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StreamingUpgradePromptProps {
  /** The locked period that the reader clicked. */
  days: number;
  onClose: () => void;
}

/**
 * The upgrade prompt at the period picker (design f6). It names the cheapest plan that shows the
 * period. "See plans" opens the upgrade modal, which also has the version for a member.
 */
const StreamingUpgradePromptComponent: React.FC<StreamingUpgradePromptProps> = ({ days, onClose }) => {
  const intl = useIntl();
  const { plan: currentPlan, historyDaysLimit } = useMusic24Capabilities();
  const [isModalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const tier = planForStreamingDays(days);
  const planName = intl.formatMessage(planNameMessages[tier]);
  const planDays = music24PlanCapabilities[tier].historyDaysLimit;

  return (
    <div className={styles.upgrade} role="status" data-testid="streaming-upgrade">
      <span className={styles.lockChip}>
        <FontAwesomeIcon icon={faLock} />
        {intl.formatMessage(messages.upgradeChip, { plan: planName })}
      </span>
      <b>{intl.formatMessage(messages.upgradeTitle, { days: planDays })}</b>
      <p>
        {withBrandWord(
          intl.formatMessage(messages.upgradeText, {
            currentPlan: intl.formatMessage(planNameMessages[currentPlan]),
            currentDays: historyDaysLimit,
          })
        )}
      </p>
      <div className={styles.upgradeActions}>
        <SMLButton variant="primary" onClick={openModal}>
          {intl.formatMessage(messages.upgradeButton)}
        </SMLButton>
        <SMLButton onClick={onClose}>{intl.formatMessage(messages.upgradeNotNow)}</SMLButton>
      </div>
      <SMLUpgradeModal
        isOpen={isModalOpen}
        onClose={closeModal}
        feature={SMLUpgradeFeature.history}
        requiredHistoryDays={days}
      />
    </div>
  );
};

export const StreamingUpgradePrompt = TypedMemo(StreamingUpgradePromptComponent);
